import React, { useMemo, memo, FC } from 'react';
import { renderToString } from 'react-dom/server';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { LOAN_TABLE_DATE_FORMAT } from 'src/constants/globalConstants';
import { hexToRgba } from 'src/helpers/colorHelpers';
import { formatMoney, formatDateFromJSON } from 'src/helpers';
import styles from './StatisticsColumnChart.module.scss';
import { GroupBy } from 'src/constants/statisticsConstants';

export type IGraphData = {
  name: string;
  percentage?: number;
  date: string;
  y: number;
};

interface IGraphsProps {
  data: Array<IGraphData>;
  type: GroupBy;
  xLabelType?: 'date' | 'direct' | 'money';
  yLabelType?: 'money' | 'percent';
  yAxisType?: 'linear' | 'logarithmic';
  currency?: string;
  id?: string;
  externalChartOptions?: EChartsOption;
}

const StatisticsColumnChart: FC<IGraphsProps> = memo(
  ({ id, data, type, xLabelType, yLabelType, yAxisType, currency, externalChartOptions }): JSX.Element => {
    const options: EChartsOption = useMemo(
      () =>
        isEmpty(externalChartOptions)
          ? getChartConfig({ data, xLabelType, yLabelType, yAxisType, currency, id })
          : merge({}, getChartConfig({ data, xLabelType, yLabelType, yAxisType, currency, id }), externalChartOptions),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currency, data, xLabelType, yLabelType, yAxisType, externalChartOptions]
    );

    return (
      <div className={styles.graphsWrapper}>
        {/* //* current usage of conditional rendering is needed to redraw chart correctly, shouldn't change it. */}
        {type === GroupBy.DAY && <ReactECharts option={options} style={{ width: '100%', height: '100%' }} />}
        {type === GroupBy.MONTH && <ReactECharts option={options} style={{ width: '100%', height: '100%' }} />}
        {type === GroupBy.WEEK && <ReactECharts option={options} style={{ width: '100%', height: '100%' }} />}
        {type === GroupBy.YEAR && <ReactECharts option={options} style={{ width: '100%', height: '100%' }} />}
        {typeof type === 'undefined' && <ReactECharts option={options} style={{ width: '100%', height: '100%' }} />}
      </div>
    );
  }
);

StatisticsColumnChart.displayName = 'StatisticsColumnChart';

type TGetChartConfigPayload = {
  data: IGraphData[];
  currency: string;
  xLabelType?: 'date' | 'direct' | 'money';
  yLabelType?: 'money' | 'percent';
  yAxisType?: 'linear' | 'logarithmic';
  id?: string;
};

const getChartConfig = ({
  data,
  xLabelType,
  yLabelType,
  yAxisType = 'linear',
  currency
}: TGetChartConfigPayload): EChartsOption => {
  const axisType = yAxisType === 'logarithmic' ? 'log' : 'value';

  return {
    color: [hexToRgba('#077186', 0.9)],
    grid: { left: 16, right: 16, top: 16, bottom: 16, containLabel: true },
    xAxis: {
      type: 'category',
      axisTick: { show: true },
      axisLabel: {
        fontFamily: 'Verlag, sans-serif',
        formatter: (value: string, index: number) => {
          const currentObject = data[index];

          if (xLabelType === 'money') return `${formatMoney(value, currency)}`;
          if (xLabelType === 'direct') return `${currentObject?.date ?? ''}`;

          return currentObject?.date
            ? `${formatDateFromJSON(currentObject?.date, LOAN_TABLE_DATE_FORMAT)}`
            : `${value}`;
        }
      },
      data: data.map((item) => item.name)
    },
    yAxis: {
      type: axisType,
      axisLabel: {
        fontFamily: 'Verlag, sans-serif',
        formatter: (value: number) => {
          if (yLabelType === 'percent') return `${value}%`;

          return `${formatMoney(value, currency)}`;
        }
      }
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#fff',
      borderColor: '#d0d0d0',
      textStyle: { color: '#111', fontFamily: 'Verlag, sans-serif' },
      extraCssText: 'font-family:Verlag,sans-serif;',
      formatter: (params) => {
        const point = params as {
          color: string;
          data: IGraphData;
        };

        return renderToString(
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: '3px', fontFamily: 'Verlag, sans-serif' }}>
            <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1 }}>{point?.data?.date ?? ''}</span>

            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1, color: point.color }}>
              {yLabelType === 'percent' ? `${point?.data?.name}%` : formatMoney(point?.data?.name, currency)}
            </span>

            {!isNil(point?.data?.percentage) && (
              <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1 }}>
                <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1 }}>
                  {point?.data?.percentage?.toFixed(2)}%
                </span>{' '}
                of total
              </span>
            )}
          </div>
        );
      }
    },
    legend: { show: false },
    series: [
      {
        type: 'bar',
        barMinHeight: 0,
        data: data.map((point) => ({ ...point, value: point.y }))
      }
    ]
  };
};

export default StatisticsColumnChart;
